import React, {useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {Dinero, toDecimal} from "dinero.js";
import {useFormContext, useWatch} from "react-hook-form";
import {Button, Icon} from "peggirkit";
import {PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/16/solid";
import {PosOrderDiscount, PosOrderForm} from "@/data/entities/order";
import {formatMoney} from "@/utils/MoneyUtils";
import DiscountModal from "@widgets/pos/order/summary/discount/DiscountModal";

type Props = {
    // Calculated discount, see cartToTotals
    amount: Dinero<number>,
    disabled: boolean,
};

/**
 * Discount row in the totals of the order editor: a button to add a
 * discount, or the current discount with buttons to change or remove it.
 */
const DiscountLine = ({amount, disabled}: Props) => {
    const intl = useIntl();
    const [showModal, setShowModal] = useState<boolean>(false);
    const {setValue} = useFormContext<PosOrderForm>();
    const discount = useWatch<PosOrderForm, "discount">({name: "discount"});

    const setDiscount = (value: PosOrderDiscount | null) =>
        setValue("discount", value, {shouldDirty: true});

    const iconButton = (icon: typeof PencilIcon, titleId: string, onClick: () => void, danger: boolean = false) => (
        <Button
            iconBefore={<Icon icon={icon}/>}
            iconOnly={true}
            size={"xs"}
            variant={"text"}
            color={danger ? "danger" : "secondary"}
            borderRadius={"rounded-full"}
            disabled={disabled}
            title={intl.formatMessage({id: titleId})}
            buttonAction={{
                as: "button",
                actionProps: {
                    onClick: e => {
                        e.preventDefault();
                        onClick();
                    },
                },
            }}
        >
            <FormattedMessage id={titleId}/>
        </Button>
    );

    return (
        <>
            {
                discount === null
                    ? (
                        <div className={"flex justify-between gap-3"}>
                            <dt><FormattedMessage id={"discount"}/></dt>
                            <dd><Button
                                iconBefore={<Icon icon={PlusIcon}/>}
                                variant={"link"}
                                color={"primary"}
                                disabled={disabled}
                                buttonAction={{
                                    as: "button",
                                    actionProps: {
                                        onClick: e => {
                                            e.preventDefault();
                                            setShowModal(true);
                                        },
                                    },
                                }}
                            >
                                <FormattedMessage id={"addDiscount"}/>
                            </Button>
                            </dd>
                        </div>
                    )
                    : (
                        <div className={"flex justify-between items-center gap-3 text-green-700 dark:text-green-400"}>
                            <dt className={"flex items-center gap-1"}>
                                {
                                    discount.type === "PERCENTAGE"
                                        ? <FormattedMessage
                                            id={"discountPercentage"}
                                            values={{
                                                percentage: intl.formatNumber(discount.value, {maximumFractionDigits: 2}),
                                            }}
                                        />
                                        : <FormattedMessage id={"discount"}/>
                                }
                                {iconButton(PencilIcon, "editDiscount", () => setShowModal(true))}
                                {iconButton(TrashIcon, "removeDiscount", () => setDiscount(null), true)}
                            </dt>
                            <dd className={"tabular-nums"}>
                                {`− ${formatMoney(Number(toDecimal(amount)))}`}
                            </dd>
                        </div>
                    )
            }

            <DiscountModal
                show={showModal}
                setShow={setShowModal}
                current={discount}
                onApply={setDiscount}
            />
        </>
    );
};

export default DiscountLine;